import React, { memo, useMemo, type ReactNode } from 'react'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Paper } from '@mui/material'
import { ContractorStepDetailsRecollectButton } from './components/buttons/ContractorStepDetailsRecollectButton'
import { ContractorStepDetailsCardHeader } from './components/ContractorStepDetailsCardHeader'
import { ContractorStepDetailsCardContent } from './components/ContractorStepDetailsCardContent'
import { ContractorStepDetailsDelayedBanner } from './components/ContractorStepDetailsDelayedBanner'
import { ContractorStepDetailsCardStatus, ContractorStepDetailsCardUsageVariant } from './constants'
import type { CollectedData } from 'src/interfaces/collectedData'
import { CollectedDataStatus } from 'src/constants/collectedData'
import type { ContractorDetails, ContractorRecollection } from 'src/interfaces/contractor'
import { CustomerCollectedDataTypeExtended } from 'src/constants/customer/enums'
import { SubmissionBlock } from './types'

dayjs.extend(utc)

export interface ContractorStepDetailsCardProps {
  variant: ContractorStepDetailsCardUsageVariant
  stepType?: CustomerCollectedDataTypeExtended
  status: ContractorStepDetailsCardStatus
  actions?: ReactNode
  collectedData?: CollectedData | null
  contractorDetails?: ContractorDetails | null
  onRefreshContractors?: () => void | Promise<void>
  showRecollectButton?: boolean
  expiresAt?: string
  showDelayedBanner?: boolean
  isRecollecting?: boolean
  recollectionData?: ContractorRecollection[]
  children?: ReactNode
  onReviewStepDataAction?: () => void | Promise<void>
  className?: string
  loading?: boolean
  contentBlocks?: SubmissionBlock[]
  isOptional?: boolean
  readOnly?: boolean
}

const ContractorStepDetailsCardComponent = ({
  variant,
  stepType,
  status,
  actions,
  collectedData,
  contractorDetails,
  onRefreshContractors,
  showRecollectButton = false,
  expiresAt,
  showDelayedBanner = false,
  isRecollecting,
  recollectionData,
  children,
  onReviewStepDataAction,
  className,
  loading = false,
  contentBlocks,
  isOptional,
  readOnly = false,
}: ContractorStepDetailsCardProps) => {
  const shouldShowRecollectButton =
    !readOnly &&
    showRecollectButton &&
    onRefreshContractors != null &&
    collectedData != null &&
    contractorDetails != null

  const contentBlocksToPass = useMemo((): SubmissionBlock[] => {
    if (contentBlocks != null && contentBlocks.length > 0) {
      return contentBlocks
    }

    const singleBlock: SubmissionBlock = {
      status,
      showReviewButtonWithText: collectedData?.status === CollectedDataStatus.WaitingForApproval,
      expiresAt,
      effectiveAt: collectedData?.effectiveAt
        ? dayjs(collectedData.effectiveAt).utc().format('MM/DD/YYYY')
        : undefined,
      collectedData,
      actions: readOnly ? undefined : actions,
      showExpirationDate: false,
      showEffectiveDate: false,
      onReviewStepDataAction: readOnly ? undefined : onReviewStepDataAction,
    }

    const hasContent =
      singleBlock.status != null ||
      singleBlock.expiresAt ||
      singleBlock.effectiveAt ||
      onReviewStepDataAction ||
      actions ||
      collectedData?.lastRejectionReason

    return hasContent ? [singleBlock] : []
  }, [contentBlocks, status, expiresAt, collectedData, onReviewStepDataAction, actions, readOnly])

  return (
    <Paper
      component="article"
      elevation={0}
      variant="outlined"
      className={className}
      css={[
        tw`bg-white rounded-2xl overflow-hidden border-0 p-4 w-full max-w-full min-w-[426px]`,
        loading && tw`opacity-60 pointer-events-none`,
      ]}
      aria-busy={loading}
    >
      {showDelayedBanner && <ContractorStepDetailsDelayedBanner />}

      <ContractorStepDetailsCardHeader
        usageVariant={variant}
        stepType={stepType}
        collectedData={collectedData}
        status={status}
        isRecollecting={isRecollecting}
        recollectionData={recollectionData}
        isOptional={isOptional}
        paymentMethodUpdatedAt={
          contractorDetails?.paymentMethod?.updatedAt || contractorDetails?.paymentMethod?.createdAt
        }
      />

      {children && <div css={tw`mt-4`}>{children}</div>}

      {shouldShowRecollectButton && (
        <ContractorStepDetailsRecollectButton
          collectDataStep={collectedData.collectDataStep}
          partnerDriverId={contractorDetails.id}
          onAction={onRefreshContractors}
        />
      )}

      <ContractorStepDetailsCardContent
        usageVariant={variant}
        contentBlocks={contentBlocksToPass}
        readOnly={readOnly}
      />
    </Paper>
  )
}

export const ContractorStepDetailsCard = memo(ContractorStepDetailsCardComponent)
